import type { AgentSessionApiId, SendBundleValidationResult } from '@/model/Agent.model';
import { sendDatasetOf } from '@/components/Agents/SendValidator/utils/SendDomains';
import SendCoercionWarnings, {
  SEND_COERCION_REPORT_REMEDY,
} from '@/components/Agents/SendValidator/SendCoercionWarnings';
import { resultCoercionFilesOf } from '@/components/Agents/SendValidator/utils/SendCoercion';
import { _isNotEmpty, _notNil } from '@/littledash';
import { useMemo, type FC, type PropsWithChildren } from 'react';
import SendAiNotes from './SendAiNotes';
import SendCrossDomainResults from './SendCrossDomainResults';
import SendNotEvaluated from './SendNotEvaluated';
import SendReportLayout from './SendReportLayout';
import SendResultsGrid from './SendResultsGrid';
import SendRuleChecks from './SendRuleChecks';
import SendRunHeader from './SendRunHeader';
import SendRunScope from './SendRunScope';
import SendWhatTheReportHolds from './SendWhatTheReportHolds';
import { aiReviewCoverageTextOf, aiReviewOutcomeOf } from '@/components/Agents/SendValidator/utils/SendAiUse';
import { crossDomainTierOf, exactPassOf } from '@/components/Agents/SendValidator/utils/SendStoredReport';
import { fileKeysOf } from '@/components/Agents/SendValidator/utils/SendText';
import {
  fileRuleCheckOf,
  hasUnsettledRules,
  proxyRulesOf,
  unsettledRulesOf,
} from '@/components/Agents/SendValidator/utils/SendRuleOutcomes';
import {
  ruleProvenanceOf,
  SendRuleProvenanceContext,
} from '@/components/Agents/SendValidator/utils/SendRuleProvenance';
import SendFdaRuleTextsProvider from './SendFdaRuleTextsProvider';
import { citesFdaValidatorRules } from '@/components/Agents/SendValidator/utils/SendFdaRuleTexts';

interface SendValidationReportProps {
  result: SendBundleValidationResult;
  /** The session the result belongs to, so the FDA's text for the rules it cites can be fetched. */
  sessionApiId?: AgentSessionApiId;
  testId?: string;
}

/** Fetches the FDA texts only for a session whose result cites an FDA rule; otherwise the ids show alone. */
const FdaRuleTexts: FC<PropsWithChildren<{ sessionApiId?: AgentSessionApiId }>> = ({ sessionApiId, children }) =>
  _notNil(sessionApiId) ? (
    <SendFdaRuleTextsProvider sessionApiId={sessionApiId}>{children}</SendFdaRuleTextsProvider>
  ) : (
    children
  );

/**
 * A SEND bundle validation, top to bottom: the run, how the upload fared against the published
 * rules, one section per file, the cross-domain checks, and Benchling In Vivo AI's notes apart from
 * all of them, with the rules the run did not settle and the report's exact totals ahead of the
 * files. Every number is the server's. The scope region comes first after the run: whether the study
 * could be validated at all, what it declared, and the pre-checks related to the FDA's rejection
 * criteria, then any files that look saved in Excel, a warning beside the findings, never one of them.
 */
const SendValidationReport: FC<SendValidationReportProps> = ({ result, sessionApiId, testId }) => {
  const proxyRules = useMemo(() => proxyRulesOf(result.report), [result]);
  const provenance = useMemo(() => ruleProvenanceOf(result.report), [result]);
  const aiUsed = result.run_metadata?.llm_used === true;
  const fileKeys = useMemo(() => fileKeysOf(result.per_domain), [result]);
  // The rule tally is per dataset, so two files of one dataset share one tally between them.
  const datasetFiles = useMemo(
    () =>
      result.per_domain.reduce<Map<string, number>>(
        (counts, file) => counts.set(sendDatasetOf(file), (counts.get(sendDatasetOf(file)) ?? 0) + 1),
        new Map()
      ),
    [result]
  );
  const exactPass = useMemo(() => exactPassOf(result.report, result.scope), [result]);
  const citesFdaRules = useMemo(() => citesFdaValidatorRules(result), [result]);
  const hasUnsettled = useMemo(() => hasUnsettledRules(unsettledRulesOf(result.report)), [result]);
  const suspectFiles = useMemo(() => resultCoercionFilesOf(result.per_domain), [result]);

  return (
    <FdaRuleTexts sessionApiId={citesFdaRules ? sessionApiId : undefined}>
      <SendRuleProvenanceContext value={provenance}>
        <SendReportLayout
          testId={testId}
          slots={{
            header: <SendRunHeader result={result} />,
            scope: (
              <>
                <SendRunScope scope={result.scope} trcProxies={result.trc_proxies} />
                {_isNotEmpty(suspectFiles) && (
                  <SendCoercionWarnings
                    files={suspectFiles}
                    remedy={SEND_COERCION_REPORT_REMEDY}
                    testId="invivo-send-validator__coercion-warnings"
                  />
                )}
              </>
            ),
            ruleChecks: (
              <div className="ui-card ph4 pb4 pt1">
                <SendRuleChecks report={result.report} tally={result.report.tally} scope={result.scope} />
              </div>
            ),
            notEvaluated: (
              <>
                {hasUnsettled && (
                  <div className="mb3">
                    <SendNotEvaluated report={result.report} />
                  </div>
                )}
                <SendWhatTheReportHolds report={result.report} />
              </>
            ),
            files: result.per_domain.map((file, index) => (
              <div key={fileKeys[index]} className={index > 0 ? 'mt3' : ''} data-test-element="per-domain-result">
                <SendResultsGrid
                  result={file}
                  ruleCheck={fileRuleCheckOf(result.report, sendDatasetOf(file), file.counts, exactPass)}
                  proxyRules={proxyRules}
                  aiUsed={aiUsed}
                  datasetFiles={datasetFiles.get(sendDatasetOf(file)) ?? 1}
                  sectionNumber={index + 1}
                  testId={`agent-send-results__results--${fileKeys[index]}`}
                />
              </div>
            )),
            crossDomain: (
              <SendCrossDomainResults
                groups={result.cross_domain}
                tier={crossDomainTierOf(result.report)}
                exactPass={exactPass?.cross_domain}
                proxyRules={proxyRules}
                undecidedChecks={result.report.cross_domain_undecided_checks}
                sectionNumber={result.per_domain.length + 1}
              />
            ),
            // The server's count decides whether there is an AI section at all, as it does inside one, and a
            // review that did not complete, or did not reach every file, is always said.
            aiNotes:
              result.report.advisory_counts.observations > 0 ||
              aiReviewOutcomeOf(result.run_metadata) === 'failed' ||
              _notNil(aiReviewCoverageTextOf(result.run_metadata)) ? (
                <SendAiNotes result={result} />
              ) : undefined,
          }}
        />
      </SendRuleProvenanceContext>
    </FdaRuleTexts>
  );
};

export default SendValidationReport;
